import { useCallback, useEffect, useState } from 'react';
import { X, UploadCloud, Loader2, Check, AlertTriangle, RefreshCw, Undo2, Package, Server } from 'lucide-react';
import { apiService, PackGroupDTO, PackContainer } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useAuthStore } from '@/stores/auth.store';
import { projectMatches } from '@/config/games';
import { clearConfigListCache } from './configListCache';
import styles from './DeployPanel.module.scss';

interface GroupChanges {
  group: PackGroupDTO;
  files: string[];
}

interface ContainerChanges {
  container: PackContainer;
  files: string[];
}

/**
 * Revue avant envoi, en 2 onglets aux procédures DISTINCTES :
 * - Ressource (behavior + resource pack) : sélection de fichiers → demande (develop → PR → main → release).
 * - Config (containers data/mods) : sélection de containers → demande (v<next> → PR CI → merge → release + Docker).
 * « Envoyer » crée des DEMANDES d'édition ; on valide/accepte ensuite dans l'espace Déploiement.
 */
export function DeployPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const requestedBy = useAuthStore((s) => s.user?.gamertag) ?? undefined;
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<'ressource' | 'config'>('ressource');
  const [groups, setGroups] = useState<GroupChanges[]>([]);
  const [containers, setContainers] = useState<ContainerChanges[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedContainers, setSelectedContainers] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [logs, setLogs] = useState<string[]>([]);

  const fileKey = (gid: string, f: string) => `${gid}::${f}`;

  const refresh = useCallback(async () => {
    setLoading(true);
    setConfirmRevert(false);
    setFeedback(null);
    // Ressource (packs) du projet actif
    const gr = await apiService.getPacksGroups();
    const list = (gr.data?.groups ?? []).filter((g) => projectMatches(g.projects, activeGameId));
    const packResults = await Promise.all(
      list.map(async (g) => ({ group: g, files: (await apiService.getPackModifiedFiles(g.id)).data?.files ?? [] })),
    );
    const packChanges = packResults.filter((r) => r.files.length > 0);
    setGroups(packChanges);
    const all = new Set<string>();
    packChanges.forEach((r) => r.files.forEach((f) => all.add(fileKey(r.group.id, f))));
    setSelected(all);
    // Config (containers) du projet actif
    const cr = await apiService.getContainers();
    const clist = (cr.data?.containers ?? []).filter((c) => (c.project ?? 'paladium') === activeGameId);
    const contResults = await Promise.all(
      clist.map(async (c) => ({ container: c, files: (await apiService.getContainerModifiedFiles(c.id)).data?.files ?? [] })),
    );
    const contChanges = contResults.filter((r) => r.files.length > 0);
    setContainers(contChanges);
    setSelectedContainers(new Set(contChanges.map((r) => r.container.id)));
    setTab(packChanges.length > 0 || contChanges.length === 0 ? 'ressource' : 'config');
    setLoading(false);
  }, [activeGameId]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  if (!open) return null;

  const toggle = (k: string) =>
    setSelected((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const toggleGroup = (gid: string, files: string[]) =>
    setSelected((s) => {
      const n = new Set(s);
      const allSel = files.every((f) => n.has(fileKey(gid, f)));
      files.forEach((f) => (allSel ? n.delete(fileKey(gid, f)) : n.add(fileKey(gid, f))));
      return n;
    });

  const toggleContainer = (id: string) =>
    setSelectedContainers((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const allPackKeys = groups.flatMap((gc) => gc.files.map((f) => fileKey(gc.group.id, f)));
  const allContainerIds = containers.map((c) => c.container.id);
  const packAllSelected = allPackKeys.length > 0 && allPackKeys.every((k) => selected.has(k));
  const containerAllSelected = allContainerIds.length > 0 && allContainerIds.every((id) => selectedContainers.has(id));
  const selectAllPacks = (on: boolean) => setSelected(on ? new Set(allPackKeys) : new Set());
  const selectAllContainers = (on: boolean) => setSelectedContainers(on ? new Set(allContainerIds) : new Set());

  const packSelected = selected.size;
  const totalSelected = packSelected + selectedContainers.size;
  const label = (f: string) => f.replace(/^(behavior|resource)\//, '');
  const nothing = groups.length === 0 && containers.length === 0;

  const renderSection = (gid: string, title: string, arr: string[]) =>
    arr.length === 0 ? null : (
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{title}</div>
        {arr.map((f) => {
          const k = fileKey(gid, f);
          return (
            <label key={k} className={styles.fileRow}>
              <input type="checkbox" checked={selected.has(k)} onChange={() => toggle(k)} />
              <span className={styles.filePath}>{label(f)}</span>
            </label>
          );
        })}
      </div>
    );

  // Annule (git restore) les éditions sélectionnées : packs (behavior + resource) ET config (containers).
  const revertAll = async () => {
    if (!confirmRevert) { setConfirmRevert(true); return; }
    setConfirmRevert(false);
    setReverting(true);
    setFeedback(null);
    setLogs([]);
    const msgs: string[] = [];
    let ok = true;
    for (const gc of groups) {
      const files = gc.files.filter((f) => selected.has(fileKey(gc.group.id, f)));
      if (files.length === 0) continue;
      const res = await apiService.revertPackFiles(gc.group.id, files);
      clearConfigListCache(gc.group.id);
      if (res.data) { ok = ok && res.data.success; msgs.push(`[${gc.group.name || gc.group.id}] ${res.data.message}`); }
      else { ok = false; msgs.push(`[${gc.group.name || gc.group.id}] ${res.error ?? 'Échec'}`); }
    }
    for (const cc of containers) {
      if (!selectedContainers.has(cc.container.id)) continue;
      const res = await apiService.revertContainerFiles(cc.container.id, cc.files);
      if (res.data) { ok = ok && res.data.success; msgs.push(`[${cc.container.name}] ${res.data.message}`); }
      else { ok = false; msgs.push(`[${cc.container.name}] ${res.error ?? 'Échec'}`); }
    }
    setReverting(false);
    setLogs(msgs);
    setFeedback({ ok, msg: ok ? 'Modifications annulées (packs + config, restaurées depuis git).' : 'Des erreurs sont survenues (voir détails).' });
    await refresh();
  };

  // Crée en un coup une DEMANDE par groupe de pack (fichiers sélectionnés) et par container sélectionné.
  const send = async () => {
    setConfirmRevert(false);
    setPublishing(true);
    setFeedback(null);
    setLogs([]);
    let n = 0;
    let ok = true;
    for (const gc of groups) {
      const files = gc.files.filter((f) => selected.has(fileKey(gc.group.id, f)));
      if (files.length === 0) continue;
      const res = await apiService.createDeployRequest({
        project: activeGameId, type: 'pack', targetId: gc.group.id, targetName: gc.group.name || gc.group.id,
        files, message: message || undefined, requestedBy,
      });
      if (res.data) n++; else ok = false;
    }
    for (const cc of containers) {
      if (!selectedContainers.has(cc.container.id)) continue;
      const res = await apiService.createDeployRequest({
        project: activeGameId, type: 'container', targetId: cc.container.id, targetName: cc.container.name,
        files: cc.files, message: message || undefined, requestedBy,
      });
      if (res.data) n++; else ok = false;
    }
    setPublishing(false);
    setFeedback({
      ok,
      msg: ok ? `${n} demande(s) créée(s). À valider dans l'espace Déploiement.` : 'Erreur lors de la création de demandes.',
    });
    await refresh();
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.head}>
          <div className={styles.title}>
            <UploadCloud size={18} /> Envoyer
          </div>
          <div className={styles.headActions}>
            <button className={styles.iconBtn} onClick={refresh} title="Rafraîchir" disabled={loading || publishing}>
              <RefreshCw size={16} className={loading ? styles.spin : undefined} />
            </button>
            <button className={styles.iconBtn} onClick={onClose} title="Fermer">
              <X size={18} />
            </button>
          </div>
        </header>

        <div className={styles.body}>
          {loading ? (
            <div className={styles.center}>
              <Loader2 size={22} className={styles.spin} /> Analyse des modifications…
            </div>
          ) : nothing ? (
            <div className={styles.center}>Aucune modification à envoyer.</div>
          ) : (
            <>
              <div className={styles.tabBar}>
                <button className={`${styles.tab} ${tab === 'ressource' ? styles.tabActive : ''}`} onClick={() => setTab('ressource')}>
                  <Package size={14} /> Ressource ({groups.length})
                </button>
                <button className={`${styles.tab} ${tab === 'config' ? styles.tabActive : ''}`} onClick={() => setTab('config')}>
                  <Server size={14} /> Config ({containers.length})
                </button>
              </div>

              {tab === 'ressource' && (
                groups.length === 0 ? (
                  <div className={styles.center}>Aucun pack modifié.</div>
                ) : (
                  <>
                    <label className={styles.selAllRow}>
                      <input type="checkbox" checked={packAllSelected} onChange={(e) => selectAllPacks(e.target.checked)} />
                      Tout sélectionner ({allPackKeys.length} fichier{allPackKeys.length > 1 ? 's' : ''})
                    </label>
                    {groups.map((gc) => {
                      const allSel = gc.files.every((f) => selected.has(fileKey(gc.group.id, f)));
                      return (
                        <div key={gc.group.id} className={styles.group}>
                          <label className={styles.groupHead}>
                            <input type="checkbox" checked={allSel} onChange={() => toggleGroup(gc.group.id, gc.files)} />
                            <span className={styles.groupBadge} style={{ borderColor: gc.group.color || '#8B3FF1', color: gc.group.color || '#8B3FF1' }}>
                              {gc.group.name || gc.group.id}
                            </span>
                            <span className={styles.groupCount}>{gc.files.length} fichier{gc.files.length > 1 ? 's' : ''}</span>
                          </label>
                          {renderSection(gc.group.id, 'Behavior pack', gc.files.filter((f) => f.startsWith('behavior/')))}
                          {renderSection(gc.group.id, 'Resource pack', gc.files.filter((f) => f.startsWith('resource/')))}
                        </div>
                      );
                    })}
                  </>
                )
              )}

              {tab === 'config' && (
                containers.length === 0 ? (
                  <div className={styles.center}>Aucun container modifié.</div>
                ) : (
                  <>
                    <label className={styles.selAllRow}>
                      <input type="checkbox" checked={containerAllSelected} onChange={(e) => selectAllContainers(e.target.checked)} />
                      Tout sélectionner ({containers.length} container{containers.length > 1 ? 's' : ''})
                    </label>
                    {containers.map((cc) => (
                      <div key={cc.container.id} className={styles.group}>
                        <label className={styles.groupHead}>
                          <input type="checkbox" checked={selectedContainers.has(cc.container.id)} onChange={() => toggleContainer(cc.container.id)} />
                          <span className={styles.groupBadge} style={{ borderColor: '#F97316', color: '#F97316' }}>{cc.container.name}</span>
                          <span className={styles.groupCount}>{cc.files.length} fichier{cc.files.length > 1 ? 's' : ''}</span>
                        </label>
                        <div className={styles.section}>
                          {cc.files.map((f) => (
                            <div key={f} className={styles.fileRow}><span className={styles.filePath}>{f}</span></div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )
              )}
            </>
          )}
        </div>

        {logs.length > 0 && (
          <div className={styles.logs}>
            {logs.map((l, i) => {
              const m = l.match(/https?:\/\/\S+/);
              return m ? (
                <a key={i} href={m[0]} target="_blank" rel="noreferrer" className={styles.logLink}>{l}</a>
              ) : (
                <div key={i} className={styles.logLine}>{l}</div>
              );
            })}
          </div>
        )}

        <footer className={styles.foot}>
          <input
            className={styles.msgInput}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Message (optionnel)"
          />
          {feedback && (
            <span className={feedback.ok ? styles.ok : styles.err}>
              {feedback.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {feedback.msg}
            </span>
          )}
          <button
            className={`${styles.revertBtn} ${confirmRevert ? styles.revertBtnArmed : ''}`}
            onClick={revertAll}
            disabled={reverting || publishing || totalSelected === 0}
            title="Restaure depuis git les éléments sélectionnés (packs + config) — annule les éditions non publiées"
          >
            {reverting ? <Loader2 size={15} className={styles.spin} /> : <Undo2 size={15} />}
            {reverting ? 'Annulation…' : confirmRevert ? `Confirmer l'annulation (${totalSelected}) ?` : 'Annuler la sélection'}
          </button>
          <button className={styles.publishBtn} onClick={send} disabled={publishing || reverting || totalSelected === 0}>
            {publishing ? <Loader2 size={15} className={styles.spin} /> : <UploadCloud size={15} />}
            {publishing ? 'Demande…' : `Demander l'envoi (${totalSelected})`}
          </button>
        </footer>
      </div>
    </div>
  );
}
